<script lang="ts">
	import { devices_status } from 'src/stores/device-runtime/status';
	import { format } from 'svelte-i18n';
	import { fade } from 'svelte/transition';
	import { beforeUpdate, onMount } from 'svelte';

	import { devices_config } from '@devices/index';
	import {
		get_gen_2_component_from_category,
		get_gen_2_component_channel_structure,
		get_local_ip,
		get_mode,
		get_status_settings_component_path,
		is_gen_2,
		real_id,
		get_device_component_index,
		isRgbwwPm,
	} from '../../../helpers/interface_helpers';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';

	import { localReq } from '../../../services/http';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';

	import { devices } from 'src/stores/lists/devices';
	import { os } from 'src/stores/session/native-bridge';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	import {
		TemperatureWheel,
		ControlsContainer,
		ButtonGroup,
		Button,
	} from '@Shelly/shelly-design-system';
	import PinUnlock from '../../../components/PinUnlock.svelte';
	import { TempButtonsTypes } from 'src/helpers/enums';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { gen1CloudReq } from 'src/services/gen_1_reqs';

	$: settingsStore = getDeviceSettings(id);
	export let device,
		status,
		disabled = false;

	const id = real_id(device.id);
	const _comp_path = get_status_settings_component_path(device) || '';
	const mode = get_mode(device, status, $settingsStore);
	const isGen2 = is_gen_2(device);

	let value = 3000;
	let cachedValue = 0;
	let loading = false;
	let local_ip = false;
	let pinLocked;
	let showModal = false;

	let callbackValue;

	$: {
		let configRange = devices_config?.[device.type]?.modes?.[mode]?.color_temp || {
			minValue: 3000,
			maxValue: 6500,
		};
		if (isRgbwwPm(device.type)) {
			valueRange = {
				minValue: $settingsStore?.[_comp_path]?.ct_range[0] || 2700,
				maxValue: $settingsStore?.[_comp_path]?.ct_range[1] || 6500,
			};
		} else if (isGen2 && $settingsStore) {
			const componentPath = get_gen_2_component_channel_structure(device);
			const settingsCTRange = $settingsStore[componentPath]?.ct_range;

			if (settingsCTRange && Array.isArray(settingsCTRange) && settingsCTRange.length === 2) {
				valueRange = {
					minValue: settingsCTRange[0],
					maxValue: settingsCTRange[1],
				};
			} else {
				valueRange = configRange;
			}
		} else {
			valueRange = configRange;
		}
	}

	let activeTempButton = '';
	let valueRange = { minValue: 3000, maxValue: 6500 };

	$: tempButtonsValues = {
		[TempButtonsTypes.WARM]: valueRange.minValue,
		[TempButtonsTypes.WHITE]: (valueRange.minValue + valueRange.maxValue) / 2,
		[TempButtonsTypes.COLD]: valueRange.maxValue,
	};

	onMount(() => {
		local_ip = get_local_ip(device.id);
	});

	beforeUpdate(() => {
		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;
		if (!isGen2 && device.category !== 'sensor' && 'undefined' !== typeof status[_comp_path]) {
			value = status[_comp_path][device.channel].temp;
		}
		if (isGen2 && 'undefined' !== typeof status[_comp_path]) {
			value = status[_comp_path].ct;
		}
	});

	const onSelectTemp = async (newTemp) => {
		if (!pinLocked) {
			if ($os === 'IOS') {
				hapticFeedback('buttonPressed');
			}
			value = newTemp;
			let isok, res;
			loading = true;

			// update status to look cool :D
			if (!isGen2) {
				cachedValue = status[_comp_path][device.channel].temp;
				status[_comp_path][device.channel].temp = value;
			} else {
				cachedValue = status[_comp_path].ct;
				status[_comp_path].ct = value;
			}

			devices_status.update_device_status({
				id,
				msg: status,
			});

			if (isGen2) {
				res = await gen2Request(device, `${get_gen_2_component_from_category(device)}.set`, {
					id: get_device_component_index(device),
					ct: value,
				});
			} else {
				if (local_ip) {
					res = await localReq(
						device,
						`${local_ip}/light/${device.channel}`,
						'post',
						{ temp: value },
						6000,
						true,
					);
				} else {
					res = await gen1CloudReq(device, device.category, {
						temp: value,
					});
				}
			}
			isok = res.isok;

			if (isok) {
				cachedValue = value;
			} else {
				if (!isGen2) {
					status[_comp_path][device.channel].temp = cachedValue;
				} else {
					status[_comp_path].ct = cachedValue;
				}
				devices_status.update_device_status({ id, msg: status });
				console.error('Device control failed');
			}
			loading = false;
		} else {
			callbackValue = newTemp;
			showModal = true;
		}
	};

	const onClickButtonInGroup = (
		type: TempButtonsTypes.WARM | TempButtonsTypes.WHITE | TempButtonsTypes.COLD,
	) => {
		activeTempButton = type;
		onSelectTemp(tempButtonsValues[type]);
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		onSelectTemp(callbackValue);
	};

	const a11yProps = {
		tempWheelAriaLabel: $format('label_temperature', { default: 'Temperature' }),
		thumbAriaLabel: $format('label_temperature_thumb', { default: 'Temperature thumb' }),
		incrementBtnAriaLabel: $format('button_increase_temperature', {
			default: 'Increase temperature',
		}),
		decrementBtnAriaLabel: $format('button_decrease_temperature', {
			default: 'Decrease temperature',
		}),
	};
</script>

<div in:fade|global={{ duration: 300 }} class={$showNewLayout ? 'pt-s-plus' : ''}>
	<TemperatureWheel
		{a11yProps}
		icon="fa-light fa-sun-bright"
		class="mb-l"
		{value}
		min={valueRange.minValue}
		max={valueRange.maxValue}
		disabled={loading || disabled}
		on:move={() => {
			if ($os === 'IOS') {
				hapticFeedback('selection');
			}
		}}
		on:value_change={(e) => onSelectTemp(e.detail.value)}
		isLoading={loading}
	/>

	{#if $showNewLayout}
		<div class="d-flex justify-content-center width-full mb-xl">
			<ButtonGroup
				id="buttongroup-y060qkt"
				aria-label={$format('button_group_temperature', { default: 'Button group temperature' })}
			>
				<Button
					class={activeTempButton === TempButtonsTypes.WARM && 'active'}
					size="m"
					variant="custom"
					customSettings={{
						background: 'var(--color-warning)',
					}}
					{disabled}
					on:click={() => onClickButtonInGroup(TempButtonsTypes.WARM)}
					>{$format('button_warm', { default: 'Warm' })}</Button
				>
				<Button
					class={activeTempButton === TempButtonsTypes.WHITE && 'active'}
					size="m"
					variant="custom"
					{disabled}
					on:click={() => onClickButtonInGroup(TempButtonsTypes.WHITE)}
					>{$format('button_white', { default: 'White' })}</Button
				>

				<Button
					class={activeTempButton === TempButtonsTypes.COLD && 'active'}
					size="m"
					variant="custom"
					customSettings={{
						background: 'var(--color-secondary)',
					}}
					{disabled}
					on:click={() => onClickButtonInGroup(TempButtonsTypes.COLD)}
					>{$format('button_cold', { default: 'cold' })}</Button
				>
			</ButtonGroup>
		</div>
	{:else}
		<ControlsContainer class="mb-xl" color={$showNewLayout ? 'color-0' : undefined}>
			<ButtonGroup
				id="buttongroup-y060qkt"
				slot="first"
				aria-label={$format('button_group_temperature', { default: 'Button group temperature' })}
			>
				<Button
					class={activeTempButton === TempButtonsTypes.WARM && 'active'}
					size="m"
					variant="custom"
					customSettings={{
						background: 'var(--color-warning)',
					}}
					{disabled}
					on:click={() => onClickButtonInGroup(TempButtonsTypes.WARM)}
					>{$format('button_warm', { default: 'Warm' })}</Button
				>
				<Button
					class={activeTempButton === TempButtonsTypes.WHITE && 'active'}
					size="m"
					variant="custom"
					{disabled}
					on:click={() => onClickButtonInGroup(TempButtonsTypes.WHITE)}
					>{$format('button_white', { default: 'White' })}</Button
				>

				<Button
					class={activeTempButton === TempButtonsTypes.COLD && 'active'}
					size="m"
					variant="custom"
					customSettings={{
						background: 'var(--color-secondary)',
					}}
					{disabled}
					on:click={() => onClickButtonInGroup(TempButtonsTypes.COLD)}
					>{$format('button_cold', { default: 'cold' })}</Button
				>
			</ButtonGroup>
		</ControlsContainer>
	{/if}
</div>

{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}
